/**
 * @jsxRuntime classic
 * @jsx jsx
 */

import { Fragment, useCallback, useMemo, useRef, useState } from 'react';

import { getDocument } from '@atlaskit/browser-apis';
import { cssMap, cx, jsx } from '@atlaskit/css';
import Heading from '@atlaskit/heading';
import Image from '@atlaskit/image';
import { type ExternalImageIdentifier, type MediaClientConfig } from '@atlaskit/media-client';
import { MediaViewer } from '@atlaskit/media-viewer';
import { Pressable, Stack } from '@atlaskit/primitives/compiled';
import { token } from '@atlaskit/tokens';

import { Carousel } from './carousel';

// MediaViewer requires a MediaClient instance, but for external images
// no real auth is needed — a stub is sufficient
const mediaClientConfig: MediaClientConfig = {
	authProvider: () => Promise.reject(new Error('Not required for external images')),
};

const styles = cssMap({
	image: {
		borderRadius: token('radius.large'),
		display: 'block',
		height: '150px',
		objectFit: 'cover',
		width: '100%',
	},
	imageButton: {
		backgroundColor: 'transparent',
		borderRadius: token('radius.large'),
		borderStyle: 'none',
		cursor: 'pointer',
		paddingTop: token('space.0'),
		paddingRight: token('space.0'),
		paddingBottom: token('space.0'),
		paddingLeft: token('space.0'),
		scrollSnapAlign: 'start',
	},
	twoImagesButton: {
		// Uses calc() to account for the gap between the images
		// `token('space.100')` = gap used by `Carousel`
		// @ts-expect-error – calc() with design tokens is not representable in the strict width type
		width: `calc((100% - ${token('space.100')}) / 2)`,
	},
	threeImagesButton: {
		// Uses calc() to account for the gap between the images
		// `token('space.100')` = gap used by `Carousel`
		// @ts-expect-error – calc() with design tokens is not representable in the strict width type
		width: `calc((100% - 2 * ${token('space.100')}) / 3)`,
	},
	fourOrMoreImagesButton: {
		flexShrink: 0,
		// Uses calc() to account for the gap between the images
		// `token('space.100')` = gap used by `Carousel`
		// @ts-expect-error – calc() with design tokens is not representable in the strict width type
		width: `calc((100% - 3 * ${token('space.100')}) / 4)`,
	},
});

function ImageButton({
	buttonStyle,
	identifier,
	onClick,
}: {
	buttonStyle?: 'twoImagesButton' | 'threeImagesButton' | 'fourOrMoreImagesButton';
	identifier: ExternalImageIdentifier;
	onClick: (identifier: ExternalImageIdentifier) => void;
}) {
	const handleClick = useCallback(() => onClick(identifier), [identifier, onClick]);

	return (
		<Pressable
			xcss={cx(styles.imageButton, buttonStyle && styles[buttonStyle])}
			onClick={handleClick}
		>
			<Image alt={identifier.name} css={styles.image} src={identifier.dataURI} />
		</Pressable>
	);
}

export const Images = function Images({
	heading,
	images,
}: {
	heading?: string;
	images: Array<{
		altText: string;
		imageUrl: string;
		sourceTitle: string;
		sourceUrl: string;
	}>;
}) {
	const [selectedItem, setSelectedItem] = useState<ExternalImageIdentifier | null>(null);
	const triggerRef = useRef<Element | null>(null);

	const identifiers: ExternalImageIdentifier[] = useMemo(
		() =>
			images.map((result) => ({
				mediaItemType: 'external-image',
				dataURI: result.imageUrl,
				name: result.altText,
			})),
		[images],
	);

	const openViewer = useCallback((identifier: ExternalImageIdentifier) => {
		triggerRef.current = getDocument()?.activeElement ?? null;
		setSelectedItem(identifier);
	}, []);

	const closeViewer = useCallback(() => {
		const trigger = triggerRef.current;
		setSelectedItem(null);
		// Restore focus after React re-renders and the MediaViewer unmounts
		requestAnimationFrame(() => {
			if (trigger instanceof HTMLElement) {
				trigger.focus();
			}
		});
	}, []);

	if (images.length === 0) {
		return null;
	}

	const isSingleImage = images.length === 1;

	const buttonStyleByCountMap: Record<number, 'twoImagesButton' | 'threeImagesButton' | 'fourOrMoreImagesButton'> = {
		2: 'twoImagesButton',
		3: 'threeImagesButton',
	};
	const buttonStyle = isSingleImage
		? undefined
		: buttonStyleByCountMap[images.length] ?? 'fourOrMoreImagesButton';

	const imageButtons = identifiers.map((identifier, index) => (
		<ImageButton
			key={`${identifier.dataURI}-${index}`}
			buttonStyle={buttonStyle}
			identifier={identifier}
			onClick={openViewer}
		/>
	));

	const viewer = selectedItem && (
		<MediaViewer
			mediaClientConfig={mediaClientConfig}
			selectedItem={selectedItem}
			items={identifiers}
			collectionName=""
			onClose={closeViewer}
		/>
	);

	if (isSingleImage) {
		return (
			<Stack space="space.100">
				{heading ? (
					<Heading as="h2" size="xsmall">
						{heading}
					</Heading>
				) : null}
				{imageButtons}
				{viewer}
			</Stack>
		);
	}

	return (
		<Fragment>
			<Carousel heading={heading}>{imageButtons}</Carousel>
			{viewer}
		</Fragment>
	);
};
